import { FilterType, mergeFilters } from "@/lib/filters";
import { useCreateDataTableOptions } from "@/components/data-table/hooks/useCreateDataTableOptions";
import { useDynamicQueryParams } from "@/hooks/useDynamicQueryParams";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { SuccessFetchListResponse } from "@/services/api";
import { APIService } from "@/services/api";
import { useContractorContext } from "../context/ContractorContext";
import { ContractorLoadingReason } from "../context/ContractorContext";
import { ContractorAssignmentsOverrideFilter } from "@/types/contractor";
import { SortingState } from "@tanstack/react-table";

interface UseContractorAssignmentsListConfig<T> {
  name: string;
  service: APIService<T, unknown>;
  contractorId: string;
  pathBuilder?: (row: T) => string;
  enabled?: boolean;
  overrideFilter?: ContractorAssignmentsOverrideFilter;
  sorting?: SortingState;
}

export const useContractorAssignmentsList = <T extends { id: string }>({
  name,
  service,
  contractorId,
  enabled = true,
  overrideFilter,
  sorting,
}: UseContractorAssignmentsListConfig<T>) => {
  const { data: contractor, loadingState: contractorLoadingState } =
    useContractorContext();

  const options = useCreateDataTableOptions<T>(name);
  const filteredOptions = useMemo(() => {
    if (!contractorId) return options;

    const filterKey = overrideFilter?.key ?? "contractor_id";
    const filterValue = overrideFilter?.value ?? contractorId;

    return {
      ...options,
      sorting: [...options.sorting, ...(sorting ?? [])],
      filters: mergeFilters(options.filters, [
        {
          id: filterKey,
          value: {
            filter: FilterType.EqualsString,
            value: String(filterValue),
          },
        },
      ]),
    };
  }, [options, contractorId, overrideFilter, sorting]);

  const queryKey = useDynamicQueryParams(name, filteredOptions);
  const queryFn = useCallback(
    ({ pageParam }: { pageParam: string | undefined }) =>
      service.getList({
        lastItem: pageParam,
        filters: filteredOptions.filters,
        sorting: filteredOptions.sorting[0],
      }),
    [service, filteredOptions.filters, filteredOptions.sorting],
  );
  const getNextPageParam = useCallback(
    (lastPage: SuccessFetchListResponse<T>) => lastPage?.last_item,
    [],
  );

  const query = useInfiniteQuery({
    queryKey,
    queryFn,
    initialPageParam: undefined,
    getNextPageParam,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    enabled: enabled && !!contractorId,
  });

  const loadingState = useMemo(
    () => ({
      isLoading: contractorLoadingState.isLoading || query.isLoading,
      reason: contractorLoadingState.isLoading
        ? contractorLoadingState.reason
        : query.isLoading
          ? ("fetching_contractor" as ContractorLoadingReason)
          : "unknown",
    }),
    [contractorLoadingState, query.isLoading],
  );

  return {
    query,
    filteredOptions,
    loadingState,
    contractor,
  };
};
